import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { cropImage } from '../imageHelpers/imageHelpers';

type ImageUploadContextType = {
  croppedImage: string;
  fileInputRef: RefObject<HTMLInputElement>;
  handleCancelCropper: () => void;
  handleCompleteCrop: (cropData: CropData | null) => void;
  handleFileInputClick: () => void;
  handleFileUploadComplete: (result?: string) => void;
  isCropperVisible: boolean;
  isFileLoading: boolean;
  onError?: (error: ErrorEvent) => any;
  setIsCropperVisible: Dispatch<SetStateAction<boolean>>;
  setIsFileLoading: Dispatch<SetStateAction<boolean>>;
};

export type CropData = {
  cropInfo: { sx: number; sy: number; sWidth: number; sHeight: number; rotation: number };
};

// eslint-disable-next-line unicorn/no-useless-undefined
const ImageUploadContext = createContext<ImageUploadContextType | undefined>(undefined);

const ImageUploadProvider = ({
  children,
  onCancelCropper,
  onCompleteCrop,
  onFileInputClick,
  onError,
}: {
  children: React.ReactNode;
  onCancelCropper?: () => void;
  onCompleteCrop: (croppedImage: Blob) => void;
  onFileInputClick?: () => void;
  onError?: (error: ErrorEvent) => any;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCropperVisible, setIsCropperVisible] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string>('');
  const [isFileLoading, setIsFileLoading] = useState(false);

  const handleFileInputClick = () => {
    // onFileInputClick is where we'd handle any extra logic needed for the input click (such as analytics)
    if (onFileInputClick) {
      onFileInputClick();
    }
    fileInputRef.current?.click();
  };

  // triggered after the image has successfully uploaded. On success, opens the cropper modal
  const handleFileUploadComplete = (result?: string) => {
    if (result) {
      setCroppedImage(result);
    }
    setIsCropperVisible(true);
  };

  const handleCompleteCrop = (cropData: CropData | null) => {
    setIsCropperVisible(false);
    const cropImageCallback = (updatedCroppedImage: Blob | null) => {
      if (updatedCroppedImage) {
        onCompleteCrop(updatedCroppedImage);
      }
    };
    if (cropData) {
      cropImage({ image: croppedImage, cropData, callback: cropImageCallback });
    }
  };

  const handleCancelCropper = () => {
    setIsCropperVisible(false);
    setCroppedImage('');
    if (onCancelCropper) {
      onCancelCropper();
    }
  };

  const value = {
    croppedImage,
    fileInputRef,
    handleCancelCropper,
    handleCompleteCrop,
    handleFileInputClick,
    handleFileUploadComplete,
    isCropperVisible,
    isFileLoading,
    onError,
    setIsCropperVisible,
    setIsFileLoading,
  };

  return <ImageUploadContext.Provider value={value}>{children}</ImageUploadContext.Provider>;
};

const useImageUpload = () => {
  const context = useContext(ImageUploadContext);

  if (context === undefined) {
    throw new Error('useImageUpload must be used within a ImageUploadContext');
  }

  return context;
};

export { ImageUploadProvider, useImageUpload };
